import { useDispatch, useSelector } from "react-redux";
import { InputField } from "../../../components/form/InputField";
import { FormEvent, useEffect, useState } from "react";
import Button from "../../../components/button/Button";
import ArrowSmall from "../../../components/svg/ArrowSmall";
import {
	selectActiveReservation,
	setAccountId,
	updateActiveReservation,
	updateCompagnions,
} from "../../../features/reservation/reservationSlice";
import { post } from "../../../helpers/api";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../../components/svg/Loader";
import { showSnackbar } from "../../../helpers/notification";
import {
	clearLoadingOverlay,
	setLoadingOverlay,
} from "../../../features/application/appSlice";
import { RadioList } from "../../../components/form/RadioList";
import {
	selectResource,
} from "../../../helpers/reservation";
import { ICustomer } from "../../../features/reservation/reservationInterface";

export default function CustomerForm() {
	let { index = "0" } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const activeReservation = useSelector(selectActiveReservation);
	const [loading, setLoading] = useState<boolean>(false);
	// const [customerIndex, setCustomerIndex] = useState<number>(0);
	const [customer, setCustomer] = useState({
		customerId: "",
		firstName: "",
		lastName: "",
		email: "",
		sex: "",
	});
	const [errorMessage, setErrorMessage] = useState({
		firstName: "",
		lastName: "",
		email: "",
		sex: "",
	});

	// Set customer index. Should be a number
	useEffect(() => {
		if (index !== undefined) {
			resetCustomer();
		}
	}, [index]);

	// Set the customer data when customerIndex changes
	useEffect(() => {
		const customerIndex = parseInt(index);
		if (activeReservation?.Compagnions !== null && activeReservation?.Compagnions[customerIndex] !== undefined) {
			setCustomer((customer) => ({
				...customer,
				...{
					customerId: activeReservation.Compagnions[customerIndex].CustomerId,
					firstName: activeReservation.Compagnions[customerIndex].FirstName,
					lastName: activeReservation.Compagnions[customerIndex].LastName,
					email: activeReservation.Compagnions[customerIndex].Email,
					sex: activeReservation.Compagnions[customerIndex].Sex,
				},
			}));
		}
	}, [index]);

	const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		updateCustomer();
	};

	const updateCustomer = () => {
		/**
		 * Update customer info
		 * Get openbills on success
		 */
		if (activeReservation) {
			setLoading(true);
			dispatch(
				setLoadingOverlay({
					show: true,
					message: "Saving your details",
					seconds: 10,
				}),
			);
      
			post({
				url: "compagnion/update",
				params: {
					...customer,
					...{ reservationId: activeReservation?.ReservationId as string },
				},
			})
				.then((response) => {
					if (response.data && response.data.Success) {
            // Check if old customer ID is the same as the reservation acount id
            if(customer.customerId == activeReservation.AccountId) {
              // Check if the customer id is in the response
              if(!response.data.Compagnions.find((c: ICustomer) => c.CustomerId == customer.customerId)) {
                // If not found update the account id and active reservation account id
                dispatch(setAccountId(response.data.Compagnions[0].CustomerId));
                dispatch(updateActiveReservation({
                  ...activeReservation,
                  AccountId: response.data.Compagnions[0].CustomerId
                }));
              }
            }

						dispatch(updateCompagnions(response.data.Compagnions));
						goToNextPage(false);
						setLoading(false);
						dispatch(clearLoadingOverlay());
					} else {
						showSnackbar(response.data.Message, "error", "top-center");
						setLoading(false);
						dispatch(clearLoadingOverlay());
					}
				})
				.catch((error) => {
					dispatch(clearLoadingOverlay());
					showSnackbar(error.response?.data.message, "error", "top-center");
					setErrorMessage(error.response?.data.errors);
					setLoading(false);
				});
		}
	};

	const goToNextPage = (skip: boolean) => {
		if (activeReservation) {
			// Go to next customer
			if (
				activeReservation.PersonCount > parseInt(index) + 1 &&
				skip === false
			) {
				navigate(`/kiosk/check-in/customer-form/${parseInt(index) + 1}`);
			} else {
				if (activeReservation.PreAuth) {
					navigate(selectResource(activeReservation));
				} else {
					navigate(`/kiosk/check-in/consent`);
				}
			}
		}
	};

	const setCustomerValues = (name: string, value: string | number) => {
		setCustomer({ ...customer, [name]: value });
		setErrorMessage({ ...errorMessage, [name]: "" });
	};

	const resetCustomer = () => {
		setCustomer((customer) => ({
			...customer,
			...{
				customerId: "",
				firstName: "",
				lastName: "",
				email: "",
				sex: "",
			},
		}));
		setErrorMessage((error) => ({
			...error,
			...{
				firstName: "",
				lastName: "",
				email: "",
				sex: "",
			},
		}));
	};

	return (
		<div className="flex-grow flex justify-center items-center flex-col w-full">
			<form
				onSubmit={(e) => {
					handleFormSubmit(e);
				}}
				className="w-full"
			>
				<div className="text-h1 text-center">
					Traveller {parseInt(index) + 1},
					<br /> Please update your details.
				</div>
				<div className="mt-6 w-full space-y-4">
					<RadioList
						name="sex"
						label="Sex"
						disabled={loading}
						defaultValue={customer.sex}
						value={customer.sex === null ? "NULL" : customer.sex}
						options={{ "Mr.": "Male", "Ms.": "Female", "Mx.": "NULL" }}
						errorMessage={errorMessage?.sex}
						onChange={(e) => {
							setCustomerValues("sex", e.target.value);
						}}
					/>

					<InputField
						type="text"
						name="firstName"
						label="First name"
						placeholder="First name"
						disabled={loading}
						defaultValue={customer.firstName}
						value={customer.firstName}
						errorMessage={errorMessage?.firstName}
						onChange={(e) => {
							setCustomerValues("firstName", e.target.value);
						}}
					/>

					<InputField
						type="text"
						name="lastName"
						label="Last name"
						placeholder="Last name"
						disabled={loading}
						defaultValue={customer.lastName}
						value={customer.lastName}
						errorMessage={errorMessage?.lastName}
						onChange={(e) => {
							setCustomerValues("lastName", e.target.value);
						}}
					/>

					<InputField
						type="text"
						name="email"
						label="Email address"
						placeholder="Email address"
						disabled={loading}
						defaultValue={customer.email}
						value={customer.email}
						errorMessage={errorMessage?.email}
						onChange={(e) => {
							setCustomerValues("email", e.target.value);
						}}
					/>
				</div>
				<div className="mt-12">
					<Button
						text={`Save${
							activeReservation?.PersonCount &&
							parseInt(index) + 1 < activeReservation.PersonCount
								? " and check next traveller"
								: ""
						}`}
						suffix={loading ? <Loader /> : <ArrowSmall />}
						type="submit"
					/>
					{parseInt(index) > 0 &&
						activeReservation?.Compagnions &&
						activeReservation.Compagnions.length <
							activeReservation.PersonCount && (
							<div className="mt-6">
								<Button
									type="button"
									text={`My roommate arrives later / I am alone.`}
									style="secondary"
									onClick={() => goToNextPage(true)}
									suffix={loading ? <Loader /> : <ArrowSmall />}
								/>
							</div>
						)}
				</div>
			</form>
		</div>
	);
}
